import { useCallback } from "react";
import type { User } from "~/api/model";
import { useListUsers } from "~/api/users/users";
import { useOrganizationParams } from "~/helpers";

/**
 * Returns the user's full name if available, otherwise falls back to the email prefix.
 */
export function getDisplayNameByEmail(
  email: string,
  users: User[] | undefined
): string {
  const emailPrefix = email.split("@")[0];

  if (!users) {
    return emailPrefix;
  }

  const matchedUser = users.find(
    (user) => user.email.toLowerCase() === email.toLowerCase()
  );

  return matchedUser?.name || emailPrefix;
}

/**
 * Hook that provides a function to resolve an email address to a user's display name.
 * Uses the organization's user list to look up full names.
 * Falls back to email prefix if the user is not found or has no name set.
 */
export function useUserDisplayName() {
  const { organizationId } = useOrganizationParams();
  const { data: users } = useListUsers(organizationId, {
    query: {
      staleTime: 5 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
    },
  });

  const getDisplayName = useCallback(
    (email: string): string => getDisplayNameByEmail(email, users),
    [users]
  );

  return { getDisplayName };
}
